/**
 * WhatsNewModal — a one-time, version-gated "what changed" notice.
 *
 * Shows the latest release's highlights the first time a user loads the app
 * after an update, then records the version so it won't show again. Brand-new
 * users (no stored version) are not nagged — we just record the current version
 * silently. Per-device via localStorage; all reads/writes are guarded.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { RELEASE_NOTES, CURRENT_RELEASE_VERSION } from '@/data/releaseNotes';

const STORAGE_KEY = 'projexhq.lastSeenRelease';

export function WhatsNewModal() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!CURRENT_RELEASE_VERSION) return;
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        // First-ever load on this device — don't interrupt a new user.
        localStorage.setItem(STORAGE_KEY, CURRENT_RELEASE_VERSION);
        return;
      }
      if (seen !== CURRENT_RELEASE_VERSION) setOpen(true);
    } catch {
      /* storage unavailable (private window, blocked) — just don't show */
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, CURRENT_RELEASE_VERSION);
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const latest = RELEASE_NOTES[0];
  if (!latest) return null;

  const footer = (
    <>
      <Button
        variant="ghost"
        onClick={() => {
          dismiss();
          navigate('/whats-new');
        }}
      >
        See all updates
      </Button>
      <Button variant="accent" onClick={dismiss}>
        Got it
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={open}
      onClose={dismiss}
      title={`What’s new in ${latest.version}`}
      size="md"
      footer={footer}
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-brand-orange" />
        <h4 className="text-base font-semibold text-ink-primary m-0">{latest.title}</h4>
      </div>
      <p className="text-xs text-ink-tertiary mb-4">{latest.date}</p>
      <ul className="space-y-2.5">
        {latest.highlights.map((h, i) => (
          <li key={i} className="flex gap-2 text-sm text-ink-secondary">
            <span className="text-brand-orange mt-0.5 shrink-0">•</span>
            <span>
              {h.text}
              {h.pro && (
                <Badge tone="navy" size="sm" className="ml-1.5 align-middle">
                  Pro
                </Badge>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
